import React, { useEffect, useState } from 'react';
import { InternalApiBatch, UploadBatchDelimiterEnum, UploadBatchTypeEnum } from '../api-internal';
import { batchApi } from '../apis';
import { Form as ReactForm } from 'react-bootstrap';
import { SalvModal } from '@salvapp/ui-components';
import Table from '../table/redux/Table';
import FormattedDateTime from '../common/datetime/FormattedDateTime';
import { uploadBatch } from './batchActions';
import DataUploadPreview from './DataUploadPreview';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { DataUploadDetails } from './DataUploadDetails';
import { Button, FileUpload, Size } from '@salvapp/ui-components';
import { DataUploadStatus } from './DataUploadStatus';
import { AppState } from '../store/store';
import { setRefresh } from '../table/redux/tablesReducer';
import { setProgress } from './dataUploadReducer';
import Has from '../auth/Has';
import { CAN_ADD_BATCH } from '../auth/permissions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/pro-solid-svg-icons';
import SalvPage from '../common/SalvPage';
import { getConfigurationUrl } from '../navigation/helper';
import { ConfigurationProduct } from '../settings/configuration/Configuration';

const TABLE_NAME = 'data-upload';

const fetchBatches = async (page: number, size: number) => {
  return batchApi.findBatches({ page, size });
};

const tableHeader = () => (
  <tr>
    <th>#</th>
    <th>Date</th>
    <th>File Name</th>
    <th>Type</th>
    <th>Status</th>
    <th>Details</th>
  </tr>
);

export const DataUpload = () => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDetailsOfDataUpload, setShowDetailsOfDataUpload] = useState<number | undefined>(undefined);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const uploadProgress = useSelector((state: AppState) => state.dataUpload, shallowEqual);
  const dispatch = useDispatch();

  const upload = async (dataType?: UploadBatchTypeEnum, delimiter?: UploadBatchDelimiterEnum) => {
    setIsUploading(true);
    try {
      if (file && dataType && !isUploading) {
        dispatch(uploadBatch(dataType, file, delimiter));
      }
    } finally {
      setIsUploading(false);
      setShowPreviewModal(false);
    }
  };

  useEffect(() => {
    if (!uploadProgress.inProgress && uploadProgress.uploaded) {
      dispatch(setRefresh({ name: TABLE_NAME, shouldRefresh: true }));
      dispatch(setProgress({ inProgress: false, uploaded: false }));
    }
  }, [uploadProgress.inProgress, uploadProgress.uploaded, dispatch]);

  return (
    <SalvPage
      title={'Data upload'}
      breadcrumb={{
        title: 'Configuration',
        to: getConfigurationUrl(ConfigurationProduct.GENERAL),
      }}
    >
      <Has allPermissions={CAN_ADD_BATCH}>
        <FileUpload
          onChange={(acceptedFiles: File[] | null) => {
            if (acceptedFiles && acceptedFiles.length > 0) {
              setFile(acceptedFiles[0]);
              setShowPreviewModal(true);
            }
          }}
          hideUploadedFile={true}
          accept={{ 'text/*': ['.csv'] }}
          size={Size.MEDIUM}
        >
          <div className='dz-message w-100 py-5 m-b-16'>
            <FontAwesomeIcon icon={faUpload} className='fe' />
            <p>Drag the data file to this area to upload</p>
          </div>
        </FileUpload>
        <SalvModal show={showPreviewModal} onHide={() => setShowPreviewModal(false)}>
          <ReactForm>
            <SalvModal.Header closeButton>Upload data</SalvModal.Header>
            <DataUploadPreview
              file={file}
              setShowModal={setShowPreviewModal}
              isUploading={isUploading}
              upload={upload}
            />
          </ReactForm>
        </SalvModal>
      </Has>
      <SalvModal show={Boolean(showDetailsOfDataUpload)} onHide={() => setShowDetailsOfDataUpload(undefined)}>
        <ReactForm>
          <SalvModal.Header closeButton>{`Data upload ${showDetailsOfDataUpload} details`}</SalvModal.Header>
          <DataUploadDetails dataUploadId={showDetailsOfDataUpload} />
        </ReactForm>
      </SalvModal>
      <Table<InternalApiBatch>
        title='Upload history'
        hover={false}
        name='batches'
        fetchContent={fetchBatches}
        header={tableHeader}
        body={(batches) =>
          batches.length > 0 &&
          batches.map((batch) => (
            <tr data-testid={`row-${batch.id}`} key={batch.id}>
              <td>{batch.id}</td>
              <td>
                <FormattedDateTime date={batch.createdTime} showTime />
              </td>
              <td>{batch.fileName}</td>
              <td>{batch.type}</td>
              <DataUploadStatus status={batch.status} />
              <td>
                <Button variant='white' size='small' onClick={() => setShowDetailsOfDataUpload(batch.id)}>
                  View
                </Button>
              </td>
            </tr>
          ))
        }
      />
    </SalvPage>
  );
};
